import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { z } from 'zod';
import { UnauthenticatedError } from '../utils/auth';
import {
  Destination,
  DestinationMetadataApiClient,
  DestinationProviderApiClient,
  XOutputAuxiliaryData,
} from './DestinationsApiClient';
import { TransientError } from '../types/errors';

type XSource = {
  id: string;
  broadcast?: { id: string };
  rtmp_url: string;
  rtmps_url: string;
  rtmp_stream_key: string;
  is_stream_active: boolean;
  name: string;
};

type CreateStreamSourceResponse = {
  sourceId: string;
  rtmpStreamUrl: string;
  // rtmpsStreamUrl: string;
  rtmpStreamKey: string;
  isStreamActive: boolean;
  name: string;
  source: XSource;
};

const knownTwitterErrors = z.object({
  error_type: z.enum(['preconditionFailed']),
  message: z.string().transform(() => 'User is ineligible for live streaming.'),
});

export class XApiClient implements DestinationProviderApiClient<'x'> {
  static STREAM_SOURCE_NAME = 'SE.Live Streaming Source';
  private baseURL = 'https://twitter-api-proxy.streamelements.com';
  private authContextProvider: AuthContextProvider;
  private serviceEnvironment: ServiceEnvironment;
  private destinationMetadata: Destination<'x'>;

  constructor({
    authContextProvider,
    serviceEnvironment,
    destinationMetadata,
  }: {
    authContextProvider: AuthContextProvider;
    serviceEnvironment: ServiceEnvironment;
    destinationMetadata: Destination<'x'>;
  }) {
    this.authContextProvider = authContextProvider;
    this.serviceEnvironment = serviceEnvironment;
    this.destinationMetadata = destinationMetadata;
  }
  endBroadCast(): Promise<void> {
    throw new Error('Method not implemented.');
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const { accessToken, providerId, id } = this.destinationMetadata;

    const response = await fetch(`${this.baseURL}/v1/${id}/${endpoint.replace('{providerId}', providerId)}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (response.status === 401) {
      throw new UnauthenticatedError('Unauthenticated');
    }

    const data = await response.json();

    if (!response.ok) {
      const parsed = knownTwitterErrors.safeParse(data);

      if (parsed.success) {
        throw new Error(parsed.data.message);
      }

      throw new TransientError('Received invalid response from X API');
    }

    return data;
  }

  async getRegion(): Promise<string> {
    const data = await this.makeRequest<{ region: string }>('region');
    return data.region;
  }

  async getStreamSources(): Promise<Map<string, CreateStreamSourceResponse>> {
    const data = await this.makeRequest<{ sources: XSource[] }>('users/{providerId}/sources', {
      method: 'GET',
    });

    return new Map(
      data.sources.map((source) => [
        source.id,
        {
          sourceId: source.id,
          // rtmpStreamUrl: source.rtmp_url,
          rtmpStreamUrl: source.rtmps_url,
          rtmpStreamKey: source.rtmp_stream_key,
          isStreamActive: source.is_stream_active,
          name: source.name,
          source,
        },
      ])
    );
  }

  async createStreamSource(params: { region: string; name: string }): Promise<CreateStreamSourceResponse> {
    const data = await this.makeRequest<{ source: XSource }>('users/{providerId}/sources', {
      method: 'POST',
      body: JSON.stringify(params),
    });

    return {
      sourceId: data.source.id,
      rtmpStreamUrl: data.source.rtmps_url,
      rtmpStreamKey: data.source.rtmp_stream_key,
      isStreamActive: data.source.is_stream_active,
      name: data.source.name,
      source: data.source,
    };
  }

  async createBroadcast(params: {
    region: string;
    sourceId: string;
  }): Promise<{ broadcastId: string; shareUrl: string }> {
    const data = await this.makeRequest<{ broadcast: { id: string }; share_url: string }>(
      'users/{providerId}/broadcasts',
      {
        method: 'POST',
        body: JSON.stringify({
          region: params.region,
          source_id: params.sourceId,
          is_low_latency: true,
        }),
      }
    );

    return {
      broadcastId: data.broadcast.id,
      shareUrl: data.share_url,
    };
  }

  async publishBroadcast(params: {
    broadcastId: string;
    title: string;
    chatAccessOption: XOutputAuxiliaryData['chatAccessOption'];
  }): Promise<{ broadcastId: string }> {
    const data = await this.makeRequest<{ broadcast: { id: string } }>(
      `users/{providerId}/broadcasts/${params.broadcastId}/state`,
      {
        method: 'PUT',
        body: JSON.stringify({
          title: params.title,
          chat_option: params.chatAccessOption,
          should_not_tweet: false,
          locale: 'en_US',
          state: 'PUBLISH',
        }),
      }
    );

    return {
      broadcastId: data.broadcast.id,
    };
  }

  async endBroadcast({ broadcastId }: { broadcastId: string }): Promise<void> {
    await this.makeRequest(`users/{providerId}/broadcasts/${broadcastId}/state`, {
      method: 'PUT',
      body: JSON.stringify({
        state: 'END',
      }),
    });
  }

  async deleteBroadcast(broadcastId: string): Promise<void> {
    const data = await this.makeRequest<{ success: boolean }>(`users/{providerId}/broadcasts/${broadcastId}`, {
      method: 'DELETE',
    });

    if (!data.success) {
      throw new Error(`Failed deleting broadcast '${broadcastId}'`);
    }
  }

  public async getStreamingOutputInfo({
    title,
    sourceId,
    chatAccessOption = '1',
  }: {
    title: string;
    sourceId: string;
    chatAccessOption: XOutputAuxiliaryData['chatAccessOption'];
  }) {
    const region = await this.getRegion();
    const allSources = await this.getStreamSources();
    let source = allSources.get(sourceId);

    if (!source) {
      source = await this.createStreamSource({
        name: XApiClient.STREAM_SOURCE_NAME,
        region,
      });
    }

    const broadcast = await this.createBroadcast({
      region,
      sourceId: source.sourceId,
    });

    await this.publishBroadcast({
      broadcastId: broadcast.broadcastId,
      title,
      chatAccessOption, // this.chatAccessOption, // TODO: FIGURE OUT LATER
    });

    return {
      serverUrl: source.rtmpStreamUrl,
      streamKey: source.rtmpStreamKey,
      broadcastId: broadcast.broadcastId,
      sourceId: source.sourceId,
    };
  }

  public setupStreamRoom: DestinationProviderApiClient<'x'>['setupStreamRoom'] = async (params) => {
    const { broadcastId, sourceId, ...streamingSettings } = await this.getStreamingOutputInfo({
      ...params,
      sourceId: params.xSourceId ?? 'new',
      chatAccessOption: params.xChatAccessOption ?? '1',
    });

    const destinationMetadataApiClient = new DestinationMetadataApiClient({
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
      destinationMetadata: params.destinationMetadata,
    });

    return destinationMetadataApiClient.updateDestination({
      outputs: params.destinationMetadata.outputs.map((output) => ({
        ...output,
        streamingSettings,
        auxiliaryData: {
          ...output.auxiliaryData,
          chatAccessOption: params.xChatAccessOption,
          streamTitle: params.title,
          broadcastId,
          sourceId,
        } satisfies XOutputAuxiliaryData,
      })),
    });
  };

  async updateStreamRoom(params: {
    outputId: string;
    title?: string;
    categoryId?: string;
    privacyStatus?: string;
  }): Promise<void> {
    void params;
  }

  public async getCategories({ query }: { query: string }): Promise<Map<string, string>> {
    void query;
    return new Map<string, string>();
  }

  public async getCategoryById(params: { categoryId: string }): Promise<{ id: string; displayName: string } | null> {
    void params;
    return null;
  }

  async checkUserCanStream(): Promise<boolean> {
    return Promise.resolve(true);
  }
}
